import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { FailedTenant } from '../api.js';
import { When } from './bits.js';

/**
 * Manifests on disk that failed to load.
 *
 * A tenant whose manifest does not parse is DARK: the registry has no entry for
 * it, so `/t/<id>` and `/t/<id>/mcp` answer nothing, and every agent that had the
 * URL gets a dead endpoint. Until this block existed the condition was visible
 * only in the server's boot log, which nobody reads on the day it matters — so it
 * is loud. Not a tooltip, not a badge on a row that is not there: the tenant is
 * not in the table at all, which is exactly why it needs its own.
 *
 * It lives on `#/host` rather than on Sites, because it reports other people's
 * sites and Sites is a customer's first screen; what a customer's screen shows
 * instead is a one-line alert linking here.
 *
 * The parse error is printed verbatim. `checkReferentialIntegrity` returns
 * sentences like `capability "x" advertises input "film" that no template reads`
 * — the message IS the repair instruction, and summarising it would throw away
 * the only useful thing in it.
 */
export function FailedBlock(props: { failed: FailedTenant[]; now: number }): VNode | null {
  if (!props.failed.length) return null;
  const n = props.failed.length;

  return html`
    <section class="dark" aria-labelledby="dark-h">
      <h2 class="dark__h" id="dark-h">
        ${n} ${n === 1 ? 'tenant is' : 'tenants are'} dark
      </h2>
      <p class="dark__p">
        ${n === 1 ? 'This manifest is' : 'These manifests are'} on disk and did not load, so the
        registry has no entry for ${n === 1 ? 'it' : 'them'}:${SP}
        <code>${'/t/<id>'}</code> and <code>${'/t/<id>/mcp'}</code> answer nothing, and any agent
        already holding the URL gets a dead endpoint. Nothing else on this page can see
        ${n === 1 ? 'it' : 'them'} — a tenant that failed to parse is absent from the Sites table
        rather than present and unhealthy.
      </p>
      <ul class="dark__list">
        ${props.failed.map(
          (f) => html`
            <li class="dark__row">
              <div class="dark__id">
                <span class="dark__tenant">${f.tenantId}</span>
                ${f.path ? html`<code class="dark__path">${f.path}</code>` : null}
              </div>
              <p class="dark__err">${f.error}</p>
              <p class="dark__meta">
                Last load attempt:${SP}
                ${When({ iso: f.at, now: props.now, absent: 'not recorded' })}
              </p>
            </li>
          `,
        )}
      </ul>
    </section>
  `;
}
